import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Icon,
  Badge,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  InfoIcon,
  CheckCircleIcon,
  StarIcon,
  ViewIcon,
  ArrowForwardIcon,
  QuestionIcon,
  SettingsIcon,
} from '@chakra-ui/icons';
import DocumentationLayout from '../../components/DocumentationLayout';
import { getDocumentationStructure } from '../../utils/docsStructure';

const DocumentationOverview: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const accentBg = useColorModeValue('blue.50', 'blue.900');

  const docStructure = getDocumentationStructure();

  const QuickStartCard: React.FC<{
    title: string;
    description: string;
    path: string;
    icon: React.ElementType;
    colorScheme: string;
  }> = ({ title, description, path, icon, colorScheme }) => (
    <Card
      bg={cardBg}
      borderColor={borderColor}
      borderWidth="1px"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
      transition="all 0.2s ease"
    >
      <CardBody>
        <VStack align="start" spacing={4}>
          <HStack>
            <Icon as={icon} color={`${colorScheme}.500`} boxSize={6} />
            <Badge colorScheme={colorScheme} variant="subtle">
              Essential
            </Badge>
          </HStack>
          
          <Box>
            <Heading size="md" mb={2}>{title}</Heading>
            <Text color={textSecondary} fontSize="sm">
              {description}
            </Text>
          </Box>
          
          <Button
            as={RouterLink}
            to={path}
            colorScheme={colorScheme}
            size="sm"
            rightIcon={<ArrowForwardIcon />}
          >
            Get Started
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );

  return (
    <DocumentationLayout
      title="Welcome to Speedcode Documentation"
      description="Learn how to solve high-performance computing challenges and optimize your code for speed."
    >
      <VStack spacing={8} align="stretch">
        {/* Introduction */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">New to Speedcode?</Text>
            <Text fontSize="sm">
              Start with our getting started guide to learn the basics of solving performance optimization problems.
            </Text>
          </Box>
        </Alert>

        {/* What is Speedcode */}
        <Box>
          <Heading size="lg" mb={4}>What is Speedcode?</Heading>
          <VStack spacing={4} align="start">
            <Text color={textSecondary}>
              Speedcode is a competitive programming platform focused on <strong>performance optimization</strong> and 
              <strong> parallel programming</strong>. Unlike traditional coding platforms that focus on algorithmic 
              correctness, Speedcode challenges you to write the fastest possible solutions.
            </Text>
            
            <Text color={textSecondary}>
              Our platform provides real-world performance engineering problems where you'll optimize algorithms for:
            </Text>
            
            <List spacing={2} pl={4}>
              <ListItem>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                <strong>Execution speed</strong> - Minimize runtime on large datasets
              </ListItem>
              <ListItem>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                <strong>Parallel efficiency</strong> - Leverage multi-core processors effectively
              </ListItem>
              <ListItem>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                <strong>Memory optimization</strong> - Reduce memory usage and improve cache performance
              </ListItem>
              <ListItem>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                <strong>Scalability</strong> - Ensure solutions work efficiently on large inputs
              </ListItem>
            </List>
          </VStack>
        </Box>

        {/* Quick Start Guide */}
        <Box>
          <Heading size="lg" mb={4}>Quick Start</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <QuickStartCard
              title="Platform Overview"
              description="Learn about Speedcode's goals, features, and how it differs from other coding platforms."
              path="/docs/overview"
              icon={InfoIcon}
              colorScheme="blue"
            />
            
            <QuickStartCard
              title="Solve Your First Problem"
              description="Step-by-step walkthrough of solving a Speedcode problem from start to finish."
              path="/docs/first-problem"
              icon={StarIcon}
              colorScheme="green"
            />
            
            <QuickStartCard
              title="Understanding Results"
              description="Learn how to interpret performance metrics, benchmark results, and submission feedback."
              path="/docs/understanding-results"
              icon={ViewIcon}
              colorScheme="purple"
            />
          </SimpleGrid>
        </Box>

        {/* Documentation Categories */}
        <Box>
          <Heading size="lg" mb={4}>Documentation Categories</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {docStructure.map((category) => (
              <Card
                key={category.id}
                bg={cardBg}
                borderColor={borderColor}
                borderWidth="1px"
              >
                <CardHeader>
                  <Heading size="md">{category.title}</Heading>
                  <Text fontSize="sm" color={textSecondary}>
                    {category.description}
                  </Text>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack spacing={2} align="start">
                    {category.sections.slice(0, 3).map((section) => (
                      <Button
                        key={section.id}
                        as={RouterLink}
                        to={section.path}
                        variant="ghost"
                        size="sm"
                        justifyContent="flex-start"
                        fontWeight="normal"
                      >
                        {section.title}
                      </Button>
                    ))}
                    {category.sections.length > 3 && (
                      <Text fontSize="xs" color={textSecondary}>
                        +{category.sections.length - 3} more sections
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>

        {/* Key Concepts */}
        <Box>
          <Heading size="lg" mb={4}>Key Concepts</Heading>
          <Card bg={accentBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <VStack align="start" spacing={3}>
                  <Heading size="md">Performance Optimization</Heading>
                  <List spacing={1} fontSize="sm">
                    <ListItem>• Algorithm complexity analysis</ListItem>
                    <ListItem>• Memory access patterns</ListItem>
                    <ListItem>• Cache-friendly data structures</ListItem>
                    <ListItem>• Vectorization opportunities</ListItem>
                  </List>
                </VStack>
                
                <VStack align="start" spacing={3}>
                  <Heading size="md">Parallel Programming</Heading>
                  <List spacing={1} fontSize="sm">
                    <ListItem>• OpenCilk parallel constructs</ListItem>
                    <ListItem>• Load balancing strategies</ListItem>
                    <ListItem>• Race condition prevention</ListItem>
                    <ListItem>• Scalability analysis</ListItem>
                  </List>
                </VStack>
              </SimpleGrid>
            </CardBody>
          </Card>
        </Box>

        {/* Getting Help */}
        <Box>
          <Heading size="lg" mb={4}>Getting Help</Heading>
          <VStack spacing={4} align="start">
            <Text color={textSecondary}>
              If you can't find what you're looking for in the documentation, here are some ways to get help:
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" p={4}>
                <HStack>
                  <Icon as={QuestionIcon} color="blue.500" />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold" fontSize="sm">Common Issues</Text>
                    <Text fontSize="xs" color={textSecondary}>
                      Check our debugging and troubleshooting guide
                    </Text>
                  </VStack>
                </HStack>
              </Card>
              
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" p={4}>
                <HStack>
                  <Icon as={SettingsIcon} color="green.500" />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold" fontSize="sm">Platform Features</Text>
                    <Text fontSize="xs" color={textSecondary}>
                      Learn about advanced tools and profiling features
                    </Text>
                  </VStack>
                </HStack>
              </Card>
            </SimpleGrid>
          </VStack>
        </Box>
      </VStack>
    </DocumentationLayout>
  );
};

export default DocumentationOverview; 